import { Decimal } from 'decimal.js'; // Assuming Decimal comes from decimal.js

/**
 * Sets a value at a nested location within an object using a dot-notation path
 * @param obj - The object to modify
 * @param propertyPath - Dot-notation path to the property (e.g. 'user.address.street')
 * @param value - The value to set at the specified path
 */
export const setNestedProperty = <T, V>(obj: T, propertyPath: string, value: V): void => {
    if (!obj || typeof obj !== 'object' || !propertyPath) {
        return;
    }

    const properties = propertyPath.split('.');
    let currentObject: any = obj;

    for (let i = 0; i < properties.length - 1; i++) {
        const property = properties[i];
        if (!(property in currentObject) || currentObject[property] === null || typeof currentObject[property] !== 'object') {
            currentObject[property] = {};
        }
        currentObject = currentObject[property];
    }

    currentObject[properties[properties.length - 1]] = value;
};

/**
 * Retrieves a value from a nested location within an object using a dot-notation path
 * @param baseObject - The object to retrieve the value from
 * @param nestedKey - Dot-notation path to the property (e.g. 'user.address.street')
 * @returns The value at the specified path or undefined if the path doesn't exist
 */
export const getNestedValue = <T = any>(baseObject: Record<string, any>, nestedKey: string): T | undefined => {
    if (!baseObject || !nestedKey) {
        return undefined;
    }

    const keys = nestedKey.split('.');
    let currentValue: any = baseObject;

    for (const key of keys) {
        if (currentValue === null || currentValue === undefined) {
            return undefined;
        }

        currentValue = currentValue[key];
    }

    return currentValue as T;
};

/**
 * Recursively merges two objects together, prioritizing source values over target values
 * but merging nested objects rather than replacing them
 * @param target - The base object to merge into
 * @param source - The object whose properties will be merged into the target
 * @returns A new object containing the merged properties
 */
export const mergeNestedObjects = <T extends Record<string, any>, U extends Record<string, any>>(target: T, source: U): T & U => {
    const result = { ...target } as T & U;

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const sourceValue = source[key];
            const targetValue = target[key]; // TODO: LOGIC-ERROR - Hard-coded special case for 'weight' property creates unexpected behavior and tight coupling. This logic should be externalized or made configurable.
            if (key === 'weight') {
                // Special case for weight property
                result[key] = new Decimal(sourceValue) as any;
            } else if (
                sourceValue &&
                typeof sourceValue === 'object' &&
                targetValue &&
                typeof targetValue === 'object' &&
                !Array.isArray(sourceValue) &&
                !Array.isArray(targetValue)
            ) {
                // Recursively merge nested objects
                result[key] = mergeNestedObjects(targetValue, sourceValue) as any;
            } else {
                // Override or add property
                result[key] = sourceValue as any;
            }
        }
    }

    return result;
};
